import React, { useState, useEffect } from 'react';
import { 
  Crown, DollarSign, TrendingUp, Users, Calendar, 
  Star, Award, BarChart3, Zap, ChevronDown, ChevronUp,
  Trophy, Medal
} from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell } from 'recharts';

const Top50HighestPaidPlayers = ({ draftData, loading, error }) => {
  const [playerData, setPlayerData] = useState([]);
  const [selectedPlayer, setSelectedPlayer] = useState(null);
  const [selectedManager, setSelectedManager] = useState(null); // For legend interaction
  const [tooltipData, setTooltipData] = useState(null); // For persistent tooltip

  // Manager color mapping for consistent styling
  const managerColors = {
    'Kevin': '#ef4444',
    'Jake': '#22c55e', 
    'Lyons': '#3b82f6',
    'Nipples': '#f59e0b',
    'Dan': '#8b5cf6',
    'Shuds': '#ec4899',
    'Karp': '#10b981',
    'Mills': '#f97316',
    'Joey': '#6366f1',
    'Raffy': '#84cc16',
    'Oster': '#06b6d4',
    'Scott': '#d946ef',
    'Sam': '#64748b'
  };

  // Process data to get top 50 highest-paid players
  useEffect(() => {
    if (!draftData || draftData.length === 0) return;

    // Group by player and calculate total investment
    const playerInvestments = draftData.reduce((acc, record) => {
      const player = record.Player?.trim() || '';
      if (!player) return acc;

      if (!acc[player]) {
        acc[player] = {
          playerName: player,
          totalCost: 0,
          draftCount: 0,
          managers: {},
          positions: new Set(),
          years: new Set(),
          records: []
        };
      }

      acc[player].totalCost += record.Cost || 0;
      acc[player].draftCount += 1;
      acc[player].positions.add(record.Position);
      acc[player].years.add(record.Year);
      acc[player].records.push(record);

      // Track manager investments
      const manager = record.Manager;
      if (!acc[player].managers[manager]) {
        acc[player].managers[manager] = {
          total: 0,
          drafted: 0,
          keeper: 0,
          years: new Set()
        };
      }
      acc[player].managers[manager].total += record.Cost || 0;
      acc[player].managers[manager].years.add(record.Year);
      if (record.Investment === 'Drafted') {
        acc[player].managers[manager].drafted += record.Cost || 0;
      } else if (record.Investment === 'Keeper') {
        acc[player].managers[manager].keeper += record.Cost || 0;
      }

      return acc;
    }, {});

    // Convert to array and get top 50
    const sortedPlayers = Object.values(playerInvestments)
      .map((player, index) => {
        // Create manager data for stacked bars - sorted by investment amount
        const managerSegments = Object.entries(player.managers)
          .map(([manager, data]) => ({
            manager,
            amount: data.total,
            color: managerColors[manager] || '#64748b',
            years: Array.from(data.years).sort((a, b) => a - b),
            drafted: data.drafted,
            keeper: data.keeper
          }))
          .sort((a, b) => b.amount - a.amount);

        return {
          ...player,
          rank: index + 1,
          primaryPosition: [...player.positions][0],
          managerCount: Object.keys(player.managers).length,
          yearSpan: player.years.size,
          yearRange: `${Math.min(...player.years)}-${Math.max(...player.years)}`,
          managerSegments, // For stacked chart
          managerData: managerSegments // Keep for compatibility
        };
      })
      .sort((a, b) => b.totalCost - a.totalCost)
      .slice(0, 50) // Ensure we get exactly 50 players
      .map((player, index) => ({ ...player, rank: index + 1 }));

    console.log('Total players processed:', Object.keys(playerInvestments).length);
    console.log('Top 50 players selected:', sortedPlayers.length);
    setPlayerData(sortedPlayers);
  }, [draftData]);

  // Show all players - no filtering
  const filteredPlayers = playerData || [];

  if (loading) {
    return (
      <div className="bg-gradient-to-br from-blue-50 to-indigo-100 rounded-3xl p-8">
        <div className="animate-pulse space-y-6">
          <div className="h-12 bg-blue-200 rounded-xl"></div>
          <div className="h-64 bg-blue-200 rounded-xl"></div>
        </div>
      </div>
    );
  }

  if (error || !playerData || playerData.length === 0) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-3xl p-8">
        <h3 className="text-lg font-medium text-red-800 mb-2">Unable to Load Player Data</h3>
        <p className="text-red-600">{error || 'No player data available'}</p>
      </div>
    );
  }

  return (
    <div className="bg-gradient-to-br from-white/10 via-white/5 to-transparent backdrop-blur-3xl rounded-2xl sm:rounded-3xl overflow-hidden transition-all duration-700 hover:shadow-3xl relative group"
         style={{
           background: `linear-gradient(135deg, rgba(59, 130, 246, 0.08), rgba(99, 102, 241, 0.06), rgba(255,255,255,0.08))`,
           boxShadow: `0 25px 50px -12px rgba(59, 130, 246, 0.15), 0 8px 32px rgba(99, 102, 241, 0.08)`
         }}>
      
      {/* Animated background elements */}
      <div className="absolute inset-0 bg-gradient-to-br opacity-40 group-hover:opacity-60 transition-opacity duration-700"
           style={{
             background: `linear-gradient(135deg, rgba(59, 130, 246, 0.15), transparent, rgba(99, 102, 241, 0.08))`
           }}></div>
      
      {/* Header Section - Mobile optimized */}
      <div className="p-4 sm:p-6 lg:p-8 relative z-10">
        <div className="text-center mb-6 sm:mb-8">
          <div className="inline-flex items-center justify-center w-12 h-12 sm:w-16 sm:h-16 rounded-full mb-3 sm:mb-4 shadow-xl"
               style={{
                 background: `linear-gradient(135deg, #3b82f6, #6366f1)`,
                 boxShadow: `0 25px 40px -10px rgba(59, 130, 246, 0.4)`
               }}>
            <Trophy className="h-6 w-6 sm:h-8 sm:w-8 text-white" />
          </div>
          <h2 className="text-xl sm:text-2xl lg:text-3xl font-black mb-2 sm:mb-3">
            <span className="bg-gradient-to-r bg-clip-text text-transparent"
                  style={{
                    backgroundImage: `linear-gradient(135deg, #3b82f6, #6366f1, #8b5cf6)`
                  }}>
              Top 50 Highest-Paid Players
            </span>
          </h2>
          <p className="text-sm sm:text-base text-gray-700 max-w-2xl mx-auto font-medium px-4 sm:px-0">
            The most valuable fantasy investments in Beasts of Bellevue history
          </p>
          <p className="text-xs sm:text-sm text-gray-600 mt-2">
            Showing all {filteredPlayers.length} players • Total invested: $
            {filteredPlayers.reduce((sum, p) => sum + (p.totalCost || 0), 0).toLocaleString()}
          </p>
        </div>
      </div>

      {/* Content Area - Mobile optimized */}
      <div className="relative z-10 px-4 sm:px-6 lg:px-8 pb-4 sm:pb-6 lg:pb-8">
        <div>
          {/* Chart View - Mobile responsive height */}
          <div className="bg-white rounded-2xl sm:rounded-3xl p-3 sm:p-4 lg:p-6 shadow-lg">
            <div className="h-[500px] sm:h-[600px] lg:h-[700px]">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart 
                  data={filteredPlayers} 
                  margin={{ 
                    top: 20, 
                    right: window.innerWidth < 640 ? 10 : 30, 
                    left: window.innerWidth < 640 ? 10 : 20, 
                    bottom: window.innerWidth < 640 ? 100 : 120 
                  }}
                >
                  <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" />
                  <XAxis 
                    dataKey="playerName" 
                    angle={-45}
                    textAnchor="end"
                    height={window.innerWidth < 640 ? 80 : 100}
                    stroke="#6b7280"
                    fontSize={window.innerWidth < 640 ? 6 : 7}
                    tickLine={false}
                    axisLine={false}
                    interval={0}
                  />
                  <YAxis 
                    stroke="#6b7280"
                    fontSize={window.innerWidth < 640 ? 9 : 11}
                    tickLine={false}
                    axisLine={false}
                    tickFormatter={(value) => `$${value}`}
                    width={window.innerWidth < 640 ? 35 : 50}
                  />
                  <Tooltip 
                    content={({ active, payload, label }) => null} // Disable default tooltip
                  />

                  {/* Create individual Bar components for each manager - this creates the stacking effect */}
                  {filteredPlayers && filteredPlayers.length > 0 && Array.from(new Set(filteredPlayers
                    .filter(p => p && p.managerSegments && Array.isArray(p.managerSegments))
                    .flatMap(p => p.managerSegments.map(m => m && m.manager).filter(Boolean))))
                    .filter(Boolean)
                    .map((manager) => (
                      <Bar 
                        key={manager}
                        dataKey={(player) => {
                          if (!player || !player.managerSegments || !Array.isArray(player.managerSegments)) return 0;
                          const managerData = player.managerSegments.find(m => m && m.manager === manager);
                          return managerData && managerData.amount ? managerData.amount : 0;
                        }}
                        stackId="investment"
                        fill={selectedManager && selectedManager !== manager ? '#e5e7eb' : managerColors[manager] || '#64748b'}
                        fillOpacity={selectedManager && selectedManager !== manager ? 0.3 : 1}
                        name={manager}
                        onClick={(data, index, event) => {
                          if (data && data.payload) {
                            const player = filteredPlayers.find(p => p.playerName === data.payload.playerName);
                            if (player) {
                              setTooltipData(player);
                            }
                          }
                        }}
                      />
                    ))}
                </BarChart>
              </ResponsiveContainer>
            </div>
            
            {/* Click instruction - Mobile friendly */}
            <div className="text-center mb-3 sm:mb-4">
              <p className="text-xs sm:text-sm text-gray-600">
                {window.innerWidth < 640 ? 'Tap any bar for details' : 'Click on any bar to view detailed manager breakdown'}
              </p>
            </div>
            
            {/* Interactive Legend for managers - Mobile responsive */}
            <div className="mt-2 flex flex-wrap justify-center gap-1 sm:gap-2">
              <div className="text-xs sm:text-sm font-semibold text-gray-700 mr-2 sm:mr-4 mb-1">Managers:</div>
              {filteredPlayers && filteredPlayers.length > 0 && Array.from(new Set(filteredPlayers
                .filter(p => p && p.managerSegments && Array.isArray(p.managerSegments))
                .flatMap(p => p.managerSegments.map(m => m && m.manager).filter(Boolean))))
                .filter(Boolean)
                .map(manager => (
                  <button
                    key={manager}
                    onClick={() => setSelectedManager(selectedManager === manager ? null : manager)}
                    className={`flex items-center px-2 sm:px-3 py-1 rounded-full transition-all duration-200 mb-1 ${
                      selectedManager === manager 
                        ? 'bg-blue-100 border-2 border-blue-400 shadow-lg scale-105' 
                        : selectedManager && selectedManager !== manager
                        ? 'bg-gray-100 opacity-50'
                        : 'bg-gray-50 hover:bg-gray-100 hover:scale-105'
                    }`}
                  >
                    <div 
                      className="w-2 h-2 sm:w-3 sm:h-3 rounded mr-1 sm:mr-2"
                      style={{ backgroundColor: selectedManager && selectedManager !== manager ? '#9ca3af' : managerColors[manager] || '#64748b' }}
                    ></div>
                    <span className={`text-xs font-medium ${
                      selectedManager === manager ? 'text-blue-800' : 'text-gray-600'
                    }`}>{manager}</span>
                  </button>
                ))}
              {selectedManager && (
                <button
                  onClick={() => setSelectedManager(null)}
                  className="ml-1 sm:ml-2 px-2 sm:px-3 py-1 bg-red-100 text-red-600 rounded-full text-xs font-medium hover:bg-red-200 transition-all duration-200 mb-1"
                >
                  Clear
                </button>
              )}
            </div>
          </div>

          {/* Persistent Tooltip Modal - Mobile optimized */}
          {tooltipData && (
            <div className="fixed inset-0 bg-black/20 backdrop-blur-sm z-50 flex items-center justify-center p-2 sm:p-4">
              <div className="bg-white/95 backdrop-blur-sm border border-gray-200 rounded-xl shadow-2xl w-full max-w-[95vw] sm:min-w-80 sm:max-w-[450px] max-h-[90vh] sm:max-h-[80vh] flex flex-col animate-in fade-in duration-200">
                {/* Header - Mobile friendly */}
                <div className="p-3 sm:p-4 border-b border-gray-200 flex items-center justify-between flex-shrink-0">
                  <div className="font-bold text-gray-900 flex items-center min-w-0">
                    <Crown className="h-4 w-4 mr-2 text-yellow-500 flex-shrink-0" />
                    <span className="truncate">#{tooltipData.rank} {tooltipData.playerName}</span>
                  </div>
                  <button
                    onClick={() => setTooltipData(null)}
                    className="text-gray-400 hover:text-gray-600 p-1 rounded-lg hover:bg-gray-100 transition-colors flex-shrink-0 ml-2"
                  >
                    <ChevronUp className="h-5 w-5" />
                  </button>
                </div>
                
                <div className="p-3 sm:p-4 flex-1 min-h-0">
                  {/* Stats Grid - Mobile responsive */}
                  <div className="grid grid-cols-2 gap-3 sm:gap-4 mb-3 sm:mb-4 flex-shrink-0">
                    <div>
                      <span className="text-gray-600 text-xs sm:text-sm">Total Investment:</span>
                      <div className="font-bold text-blue-600 text-lg sm:text-xl">${tooltipData.totalCost}</div>
                    </div>
                    <div>
                      <span className="text-gray-600 text-xs sm:text-sm">Position:</span>
                      <div className="font-semibold text-gray-800 text-sm sm:text-base">{tooltipData.primaryPosition}</div>
                    </div>
                  </div>

                  <div className="flex-1 min-h-0">
                    <div className="text-xs sm:text-sm font-semibold text-gray-700 mb-2 sm:mb-3 flex-shrink-0">
                      Manager Breakdown ({(tooltipData.managerSegments || []).length} managers):
                    </div>
                    {/* Manager list - Mobile optimized scrolling */}
                    <div className="space-y-2 overflow-y-auto pr-1 sm:pr-2" style={{ maxHeight: window.innerWidth < 640 ? '250px' : '300px' }}>
                      {(tooltipData.managerSegments || []).map((manager, index) => (
                        <div key={`${manager.manager}-${index}`} className="flex items-start justify-between p-2 sm:p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors duration-200 flex-shrink-0">
                          <div className="flex items-start min-w-0 flex-1">
                            <div 
                              className="w-3 h-3 rounded mr-2 sm:mr-3 mt-0.5 sm:mt-1 flex-shrink-0"
                              style={{ backgroundColor: manager.color }}
                            ></div>
                            <div className="min-w-0 flex-1">
                              <div className="text-xs sm:text-sm font-medium text-gray-800">{manager.manager}</div>
                              <div className="text-xs text-gray-500">
                                Years: {manager.years && manager.years.length > 0 ? manager.years.join(', ') : 'N/A'}
                              </div>
                              {manager.drafted > 0 || manager.keeper > 0 ? (
                                <div className="text-xs text-gray-400 mt-1">
                                  Drafted: ${manager.drafted || 0} | Keeper: ${manager.keeper || 0}
                                </div>
                              ) : null}
                            </div>
                          </div>
                          <div className="text-xs sm:text-sm font-bold text-blue-600 ml-2 sm:ml-3 flex-shrink-0">${manager.amount}</div>
                        </div>
                      ))}
                    </div>
                  </div>
                  
                  {/* Summary stats at bottom - Mobile responsive */}
                  <div className="border-t mt-3 sm:mt-4 pt-2 sm:pt-3 grid grid-cols-3 gap-2 text-center flex-shrink-0">
                    <div>
                      <div className="text-xs text-gray-500">Draft Years</div>
                      <div className="font-semibold text-sm">{tooltipData.draftCount || 0}</div>
                    </div>
                    <div>
                      <div className="text-xs text-gray-500">Managers</div>
                      <div className="font-semibold text-sm">{tooltipData.managerCount || 0}</div>
                    </div>
                    <div>
                      <div className="text-xs text-gray-500">Avg/Draft</div>
                      <div className="font-semibold text-sm">
                        ${tooltipData.draftCount > 0 ? Math.round(tooltipData.totalCost / tooltipData.draftCount) : 0}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Selected Player Detail Panel - Mobile optimized */}
        {selectedPlayer && (
          <div className="mt-4 sm:mt-6 bg-white rounded-2xl sm:rounded-3xl p-4 sm:p-6 shadow-lg">
            <div className="flex items-center justify-between mb-3 sm:mb-4">
              <h3 className="text-lg sm:text-xl font-bold text-gray-900 flex items-center">
                <Star className="h-5 w-5 sm:h-6 sm:w-6 mr-2 text-yellow-500" />
                <span className="truncate">{selectedPlayer.playerName} Investment</span>
              </h3>
              <button
                onClick={() => setSelectedPlayer(null)}
                className="text-gray-400 hover:text-gray-600 p-1 flex-shrink-0"
              >
                <ChevronUp className="h-5 w-5 sm:h-6 sm:w-6" />
              </button>
            </div>
            
            {/* Mobile: Stacked layout, Desktop: Grid */}
            <div className="space-y-6 lg:space-y-0 lg:grid lg:grid-cols-1 md:grid-cols-2 lg:gap-6">
              {/* Manager Breakdown */}
              <div>
                <h4 className="font-semibold text-gray-700 mb-3 text-sm sm:text-base">Manager Investments</h4>
                <div className="space-y-2">
                  {(selectedPlayer.managerSegments || []).map((manager) => (
                    <div key={manager.manager} className="flex items-center justify-between p-2 sm:p-3 bg-gray-50 rounded-lg">
                      <div className="flex items-center min-w-0 flex-1">
                        <div
                          className="w-3 h-3 sm:w-4 sm:h-4 rounded-full mr-2 sm:mr-3 flex-shrink-0"
                          style={{ backgroundColor: manager.color }}
                        ></div>
                        <div className="min-w-0">
                          <span className="font-medium text-sm sm:text-base truncate block">{manager.manager}</span>
                          <div className="text-xs text-gray-500">
                            Years: {manager.years.join(', ')}
                          </div>
                        </div>
                      </div>
                      <div className="text-right flex-shrink-0 ml-2">
                        <div className="font-bold text-blue-600 text-sm sm:text-base">${manager.amount}</div>
                        <div className="text-xs text-gray-500">
                          ${manager.drafted}D / ${manager.keeper}K
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Stats */}
              <div>
                <h4 className="font-semibold text-gray-700 mb-3 text-sm sm:text-base">Career Stats</h4>
                <div className="space-y-2 sm:space-y-3">
                  <div className="flex justify-between items-center">
                    <span className="text-gray-600 text-sm">Total Investment:</span>
                    <span className="font-bold text-blue-600 text-sm sm:text-base">${selectedPlayer.totalCost}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-gray-600 text-sm">Draft Count:</span>
                    <span className="font-semibold text-sm sm:text-base">{selectedPlayer.draftCount}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-gray-600 text-sm">Unique Managers:</span>
                    <span className="font-semibold text-sm sm:text-base">{selectedPlayer.managerCount}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-gray-600 text-sm">Years Active:</span>
                    <span className="font-semibold text-sm sm:text-base">{selectedPlayer.yearSpan}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-gray-600 text-sm">Primary Position:</span>
                    <span className="font-semibold text-sm sm:text-base">{selectedPlayer.primaryPosition}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Top50HighestPaidPlayers;